(function () {
  'use strict';

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ---- Cart drawer + accelerated-checkout gating ---------------------------
  // The delivery scheduler (MOQ, delivery fees, date/slot validation) only
  // runs on the cart page, so no surface may offer a shortcut to checkout:
  //  - Dynamic/accelerated wallet buttons (Apple Pay / Shop Pay / GPay) are
  //    GATED: hidden by default and revealed on the CART PAGE only once a valid
  //    delivery selection exists (see gateCartWallets). This keeps the wallets
  //    the client wants while preventing a customer from checking out before
  //    the delivery scheduler (date/slot/MOQ) is satisfied — no bypass. The
  //    drawer never reveals them (no scheduler there), and default-hidden
  //    avoids a flash before JS runs.
  //  - In the drawer, the standard Checkout button is also hidden and replaced
  //    with a "View cart" link that routes through the /cart scheduler.
  //  - The drawer action area is pinned to the bottom so "View cart" stays
  //    reachable no matter how many items are in the cart (fixes iPad long-
  //    list / short-viewport case where the sticky summary scrolled away).
  var style = document.createElement('style');
  style.textContent = [
    '.additional-checkout-buttons { display: none !important; }',
    '.additional-checkout-buttons.dcx-wallet-show { display: revert !important; }',
    '.cart-drawer .cart__checkout-button { display: none !important; }',
    'cart-drawer-component .cart-drawer__summary {',
    '  position: sticky !important;',
    '  bottom: 0 !important;',
    '  background: var(--color-background, #fff);',
    '  z-index: 3;',
    '  box-shadow: 0 -1px 0 rgb(var(--color-border-rgb, 0 0 0) / 0.12);',
    '}'
  ].join('\n');
  document.head.appendChild(style);

  function ensureViewCartButton() {
    document.querySelectorAll('.cart-drawer .cart__ctas').forEach(function (ctas) {
      if (ctas.querySelector('.delivery-view-cart-button')) return;
      var link = document.createElement('a');
      link.href = '/cart';
      link.className = 'button delivery-view-cart-button';
      link.innerHTML = '<span class="button-text">View cart</span>';
      ctas.insertBefore(link, ctas.firstChild);
    });
  }

  // The drawer's checkout button shares id="checkout" with the cart page's
  // button and sits earlier in the DOM (header), so getElementById-based
  // gating in the delivery scheduler could target the wrong element. The
  // drawer button is hidden above; drop its id so the cart page's button is
  // the only #checkout.
  function dedupeCheckoutId() {
    var buttons = document.querySelectorAll('#checkout');
    if (buttons.length < 2) return;
    buttons.forEach(function (btn) {
      if (btn.closest('.cart-drawer')) btn.removeAttribute('id');
    });
  }

  // The drawer content is re-rendered by Section Rendering on cart changes and
  // hydrated only when opened, which can drop the injected View cart button or
  // leave a stale #checkout id. A MutationObserver re-asserts both whenever the
  // drawer subtree changes (idempotent: ensureViewCartButton no-ops when the
  // button already exists, so this can't loop).
  function observeDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.__deliveryObserved) return;
    drawer.__deliveryObserved = true;
    new MutationObserver(function () {
      ensureViewCartButton();
      dedupeCheckoutId();
    }).observe(drawer, { childList: true, subtree: true });
  }

  // ---- Wallet (Apple Pay / Shop Pay / GPay) gating on the cart page --------
  // The wallet buttons are hidden by default (CSS) and revealed on the CART
  // PAGE only once the scheduler marks the selection valid (the
  // `delivery-scheduler-ready` class it sets on <html> once date/slot/postal or
  // pickup date are complete AND the 5-bottle minimum is met AND the fee lines
  // are synced). Wallets inside the drawer are never revealed — there's no
  // scheduler there. This keeps the wallets the client wants while making it
  // impossible to check out via a wallet before the delivery details/MOQ exist.
  function gateCartWallets() {
    var ready = document.documentElement.classList.contains('delivery-scheduler-ready');
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
      var inDrawer = !!el.closest('cart-drawer-component');
      el.classList.toggle('dcx-wallet-show', ready && !inDrawer);
    });
  }

  // React the instant the scheduler flips the ready flag on <html>. Toggling the
  // wallet class changes an element (not <html>) class, so this can't self-loop.
  new MutationObserver(gateCartWallets).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });

  // ---- Hardened checkout gate ---------------------------------------------
  // The scheduler disables #checkout when the order is invalid, but a cart
  // section re-render (morph) rewrites that button WITHOUT the disabled state,
  // briefly letting an under-MOQ / incomplete order slip through — and a
  // quantity change is only re-evaluated after a debounce, another gap. So we
  // intercept the checkout action itself, re-validate against a FRESH cart at
  // click time, and only then proceed. This is the authoritative gate; the
  // disabled attribute is now just a visual hint.
  function readSettings() {
    var el = document.getElementById('delivery-settings-data');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function schedulerReady() {
    // Set by the scheduler's setExpressGate() only when the delivery/pickup
    // selection is complete, valid, and its fee line(s) are synced.
    return document.documentElement.classList.contains('delivery-scheduler-ready');
  }

  function validateForCheckout() {
    var settings = readSettings();
    var moq = settings.moq_bottles || 0;
    // Fold in the min-order-value override (lives in a separate blob) so the
    // gate evaluates the same rule the widget does.
    if (window.JuicyDelivery) {
      var ov = JuicyDelivery.readJSON('delivery-config-overrides') || {};
      if (ov.min_order_value !== undefined && ov.min_order_value !== null) settings.min_order_value = ov.min_order_value;
      if (ov.cutoff_days !== undefined && ov.cutoff_days !== null) settings.cutoff_days = ov.cutoff_days;
      if (ov.cutoff_time) settings.cutoff_time = ov.cutoff_time;
      if (ov.pickup_lead_days !== undefined && ov.pickup_lead_days !== null) settings.pickup_lead_days = ov.pickup_lead_days;
      if (ov.promo_campaign) settings.promo_campaign = ov.promo_campaign;
    }
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var qty = (cart.items || [])
          .filter(function (i) { return !(i.properties && i.properties._delivery_fee === 'true'); })
          .reduce(function (s, i) { return s + i.quantity; }, 0);
        if (qty <= 0) return { ok: false, reason: 'empty' };
        // MOQ (with per-product exemption + Seasonal override) and the min-order
        // backstop go through the shared helper so this gate can't disagree with
        // the scheduler widget. Fallback preserves the base MOQ rule.
        if (window.JuicyDelivery) {
          if (JuicyDelivery.orderMinimumMessages(cart, settings).length) return { ok: false, reason: 'moq' };
          // Flavour cap — re-derive from the FRESH cart fetched above (same
          // rule-collection exclusion via ruleItemIds, same "only applies
          // under flavour_cap_threshold bottles" condition as
          // getCartBottleStats() in delivery-scheduler.js) instead of
          // trusting schedulerReady()'s CSS class below. Closes the exact
          // same class of stale-after-a-morph race this gate was built to
          // close for MOQ/date-window/delivery-rule — that class only
          // reflects whatever the widget last computed, which is exactly
          // the state a section re-render can leave behind stale.
          var flavourCapMax = settings.flavour_cap_max || 0;
          var flavourCapThreshold = settings.flavour_cap_threshold || 0;
          if (flavourCapMax && flavourCapThreshold && JuicyDelivery.drinkCounts(cart).total < flavourCapThreshold) {
            var ruleIds = JuicyDelivery.ruleItemIds(settings);
            var juiceItems = ruleIds.length
              ? JuicyDelivery.nonFeeItems(cart).filter(function (i) { return ruleIds.indexOf(String(i.product_id)) === -1; })
              : JuicyDelivery.nonFeeItems(cart);
            // ONE shared 5-flavour pool across the whole checkout — big
            // bottles AND bundle flavour picks together (mirrors
            // getCartBottleStats() in delivery-scheduler.js). Counts by
            // flavour NAME via JuicyDelivery.distinctFlavourCount, not
            // product_id (every bundle flavour line shares one product_id).
            var distinctFlavours = JuicyDelivery.distinctFlavourCount(juiceItems);
            if (distinctFlavours > flavourCapMax) return { ok: false, reason: 'flavour_cap' };
          }
          // A campaign product may only be fulfilled inside its on-sale window;
          // re-check the saved date here so the gate can't be raced.
          var win = JuicyDelivery.restrictedDateWindow(cart, settings);
          if (win) {
            var attrs = cart.attributes || {};
            var chosen = attrs['Delivery Date'] || attrs['Collection Date'] || '';
            if (!chosen || chosen < win.start || chosen > win.end) return { ok: false, reason: 'date_window' };
          }
          // Re-check the lead time and blocked dates from the fresh cart for
          // every order, not just the promo. The ready flag checked below can
          // be stale: a cart page left open past the cut-off, or a tap that
          // lands while the widget is still re-syncing after an edit.
          var leadAttrs = cart.attributes || {};
          var pickup = leadAttrs['Fulfilment'] === 'Self-collection';
          var selected = pickup ? leadAttrs['Collection Date'] : leadAttrs['Delivery Date'];
          if (!selected) return { ok: false, reason: 'incomplete' };
          var sg = new Date(Date.now() + 8 * 60 * 60 * 1000);
          var earliest = new Date(Date.UTC(sg.getUTCFullYear(), sg.getUTCMonth(), sg.getUTCDate()));
          if (!pickup) {
            var time = String(settings.cutoff_time || '17:00').split(':').map(Number);
            var nowSecs = sg.getUTCHours() * 3600 + sg.getUTCMinutes() * 60 + sg.getUTCSeconds();
            if (nowSecs > time[0] * 3600 + (time[1] || 0) * 60) {
              earliest.setUTCDate(earliest.getUTCDate() + 1);
            }
          }
          var leadDays = Number(pickup ? settings.pickup_lead_days : settings.cutoff_days) || 0;
          if (JuicyDelivery.promoCartState(cart, settings).present) leadDays = Math.max(3, leadDays);
          earliest.setUTCDate(earliest.getUTCDate() + leadDays);
          if (selected < earliest.toISOString().slice(0, 10)) return { ok: false, reason: 'lead_time' };
          var dateLists = JuicyDelivery.readJSON('delivery-dates-data') || {};
          var blocked = ((pickup ? dateLists.pickup_blocked_dates : dateLists.delivery_blocked_dates) || [])
            .map(function (d) { return String(d).slice(0, 10); });
          if (blocked.indexOf(selected) !== -1) return { ok: false, reason: 'blocked_date' };
          // Per-collection delivery rule (currently: mooncakes) — re-verify
          // independently of the widget's ready flag, which can be stale
          // across a section re-render or a race with its own debounced
          // recalc (the exact gap this gate exists to close for MOQ/date
          // above). In self-collection, the chosen date must still fall on
          // an allowed weekday; in delivery mode, the rule's flat fee must
          // still have a matching fee product (else the widget would be
          // charging nothing/the wrong amount for a "valid"-looking order).
          var rule = JuicyDelivery.resolveDeliveryRule(cart, settings);
          if (rule) {
            var rAttrs = cart.attributes || {};
            if (rAttrs['Fulfilment'] === 'Self-collection') {
              var cDate = rAttrs['Collection Date'] || '';
              if (!cDate || !JuicyDelivery.pickupDayAllowed(cDate, rule)) return { ok: false, reason: 'pickup_day' };
            } else {
              var feeVariants = JuicyDelivery.readJSON('delivery-fee-variants-data') || [];
              var hasFlatVariant = feeVariants.some(function (v) {
                return v.fee_type === 'base' && Number(v.price) === Math.round(Number(rule.flat_fee) * 100);
              });
              if (!hasFlatVariant) return { ok: false, reason: 'rule_misconfigured' };
            }
          }
        } else if (moq && qty < moq) {
          return { ok: false, reason: 'moq', moq: moq, qty: qty };
        }
        if (!schedulerReady()) return { ok: false, reason: 'incomplete' };
        return { ok: true };
      })
      .catch(function () { return { ok: false, reason: 'error' }; });
  }

  function focusScheduler() {
    var widget = document.querySelector('delivery-scheduler-component');
    if (widget && widget.scrollIntoView) widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function isCartPageCheckoutTrigger(e) {
    var t = e.target;
    if (!t || !t.closest) return false;
    if (t.closest('.cart-drawer')) return false; // drawer has no checkout path
    if (e.type === 'click') return !!t.closest('#checkout, .cart__checkout-button');
    if (e.type === 'submit') {
      var form = t.tagName === 'FORM' ? t : (t.closest && t.closest('form'));
      return !!form && form.id === 'cart-form';
    }
    return false;
  }

  function guardCheckout(e) {
    if (!isCartPageCheckoutTrigger(e)) return;
    // Take full control of the checkout action, then re-validate live.
    e.preventDefault();
    e.stopImmediatePropagation();
    validateForCheckout().then(function (res) {
      if (res.ok) {
        window.location.assign('/checkout');
      } else {
        focusScheduler();
      }
    });
  }

  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);

  // ---- Estimated total display sync ---------------------------------------
  // cart.total_price only includes delivery fees once the scheduler has
  // synced its fee line items (when the selection is complete and valid).
  // Until then the widget's breakdown is a preview, so the displayed
  // Estimated total is adjusted to include charges not yet in the cart.
  // Display-only: no cart writes, so this cannot feed back into the
  // scheduler's own sync loop.
  var syncTimer;

  function parseMoney(text) {
    var m = String(text || '').replace(/,/g, '').match(/\$\s*(\d+(?:\.\d+)?)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : null;
  }

  // The delivery total the widget is showing, in cents: null when there is no
  // breakdown yet, 0 when it reads "Free". Struck-through "was" amounts are
  // dropped first so a waived fee isn't read back as a charge.
  function breakdownPreviewCents(el) {
    if (!el) return null;
    var clone = el.cloneNode(true);
    clone.querySelectorAll('s').forEach(function (s) { s.remove(); });
    var text = clone.textContent.trim();
    if (!text) return null;
    var cents = parseMoney(text);
    if (cents !== null) return cents;
    return /free/i.test(text) ? 0 : null;
  }

  function syncEstimatedTotal() {
    var totals = document.querySelectorAll('.cart-totals__total-value');
    if (!totals.length) return;
    var breakdownEl = document.querySelector('.delivery-scheduler__breakdown-total');
    var previewCents = breakdownPreviewCents(breakdownEl);

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var feeCents = cart.items
          .filter(function (i) { return i.properties && i.properties._delivery_fee === 'true'; })
          .reduce(function (sum, i) { return sum + i.final_line_price; }, 0);
        // Swap whatever fee lines are in the cart for the delivery total the
        // widget is showing. Fee lines can lag behind the form (they're only
        // synced once the selection is valid), so adding just the difference
        // would overstate the total when a stale line is larger than the new fee.
        var display = money(previewCents === null ? cart.total_price : cart.total_price - feeCents + previewCents);
        totals.forEach(function (el) {
          var suffix = / SGD\s*$/.test(el.textContent) ? ' SGD' : '';
          var next = display + suffix;
          if (el.textContent.trim() !== next.trim()) {
            el.textContent = next;
            el.setAttribute('value', next);
          }
        });
      })
      .catch(function () {});
  }

  // ---- Cart tax/shipping caption -------------------------------------------
  // Delivery is charged as cart-level fee lines and the scheduler shows the
  // full breakdown above, so the stock "Taxes and shipping calculated at
  // checkout" note is wrong here — and it quietly re-raises the "will I pay
  // more later?" doubt the breakdown exists to settle. Only the cart page has
  // the scheduler, so the drawer keeps the stock wording.
  var TAX_NOTE = 'Delivery is included above. Taxes included where applicable.';

  function syncTaxNote() {
    if (!document.querySelector('delivery-scheduler-component')) return;
    document.querySelectorAll('.cart-totals__tax-note').forEach(function (el) {
      if (el.closest('cart-drawer-component')) return;
      if (el.textContent.trim() === TAX_NOTE) return;
      el.textContent = TAX_NOTE;
    });
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      ensureViewCartButton();
      dedupeCheckoutId();
      observeDrawer();
      gateCartWallets();
      syncTaxNote();
      watchBreakdown();
      syncEstimatedTotal();
    }, 250);
  }

  document.addEventListener('DOMContentLoaded', scheduleSync);
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
  document.addEventListener('cart-drawer:open', scheduleSync);
  document.addEventListener('dialog:open', scheduleSync);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest && e.target.closest('delivery-scheduler-component')) scheduleSync();
  }, true);

  // The scheduler re-renders its breakdown asynchronously after input; follow
  // its text so the displayed total keeps step. Cart page only.
  var breakdownObserver = null;
  var observedRoot = null;
  var lastSnapshot = null;

  function watchBreakdown() {
    if (observedRoot && observedRoot.isConnected) return;
    if (breakdownObserver) breakdownObserver.disconnect();
    var root = document.querySelector('delivery-scheduler-component');
    if (!root) return;
    observedRoot = root;
    breakdownObserver = new MutationObserver(function () {
      var el = root.querySelector('.delivery-scheduler__breakdown-total');
      var snapshot = el ? el.textContent : '';
      if (snapshot === lastSnapshot) return;
      lastSnapshot = snapshot;
      scheduleSync();
    });
    breakdownObserver.observe(root, { childList: true, subtree: true, characterData: true });
  }

  watchBreakdown();
  document.addEventListener('DOMContentLoaded', watchBreakdown);
})();
